import { Box, Button as MuiButton } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";

import useMediaQuery from "../../customHooks/useMediaQuery";
import { margeStyle } from "../../helper/marge-style";
import { theme } from "../../mui-customs/Theme";
import Button from "../Button";
import Slider from "../Slider";
import Typography from "../Typography";
import { PhotoEditMainProps } from "./photo-edit-main.interface";
import * as styles from "./photo-edit-main.styles";

export const PhotoEditMain = ({
  handleClose,
  image,
  handleSave,
  height,
  width,
  borderRadius = 0,
  sx,
}: PhotoEditMainProps) => {
  const [rotationValue, setRotationValue] = useState(0);
  const [zoomValue, setZoomValue] = useState(1);
  const [localImage, setLocalImage] = useState<string | File | undefined>(
    image
  );
  const [savingImage, setSavingImage] = useState(false);
  const [localWidth, setLocalWidth] = useState(width);
  const [localHeight, setLocalHeight] = useState(height);
  const [localBorder, setLocalBorder] = useState(30);

  const isMdSize = useMediaQuery("(min-width:900px)");

  useEffect(() => {
    const convertImage = async () => {
      if (image instanceof File && image.name.includes(".heic")) {
        try {
          const heic2any = require("heic2any");
          const jpegBlob = await heic2any({
            blob: image,
            toType: "image/jpeg",
          });
          setLocalImage(URL.createObjectURL(jpegBlob as Blob));
        } catch (error) {
          console.error("Error converting HEIC to JPEG:", error);
          setLocalImage(image); // Fallback to original image if conversion fails
        }
      } else {
        setLocalImage(image);
      }
    };

    convertImage();
  }, [image]);

  useEffect(() => {
    if (isMdSize) {
      setLocalHeight(height);
      setLocalWidth(width);
    } else {
      const windowWidth = window.screen.width;
      setLocalBorder(20);
      setLocalHeight((windowWidth - 80) * (height / width));
      setLocalWidth(windowWidth - 80);
    }
  }, [isMdSize, height, width]);

  const editor = useRef<AvatarEditor>(null);

  const triggerClose = () => {
    setZoomValue(1);
    setRotationValue(0);
    handleClose();
  };

  const handleSaveClick = async () => {
    if (editor.current) {
      const canvas = editor.current.getImage();
      setSavingImage(true);
      const imageFile = await fetch(canvas.toDataURL())
        .then((response) => response.blob())
        .then(
          (blob) => new File([blob], "edited_image.jpg", { type: "image/jpeg" })
        );
      handleSave(imageFile);
      setSavingImage(false);
      triggerClose();
    }
  };

  return (
    <Box sx={margeStyle(styles.root, sx)}>
      {localImage && (
        <>
          <AvatarEditor
            ref={editor}
            image={localImage}
            width={localWidth}
            height={localHeight}
            border={localBorder}
            rotate={rotationValue}
            scale={zoomValue}
            crossOrigin="anonymous"
            borderRadius={borderRadius}
          />
          <Box sx={styles.controllerContainer}>
            <Slider
              label="Rotation"
              defaultValue={0}
              minValue={0}
              maxValue={360}
              minValueLabel="0"
              maxValueLabel="360"
              value={rotationValue}
              handleOnChange={(value) => {
                typeof value === "number" && setRotationValue(value);
              }}
            />
            <Slider
              label="Zoom"
              defaultValue={1}
              minValue={1}
              maxValue={3}
              step={0.05}
              minValueLabel="1"
              maxValueLabel="5"
              value={zoomValue}
              handleOnChange={(value) => {
                typeof value === "number" && setZoomValue(value);
              }}
            />
          </Box>
          <Box sx={styles.buttonContainer}>
            <MuiButton
              variant="text"
              sx={{ width: "100%" }}
              onClick={triggerClose}
            >
              <Typography
                variant="btnMedium"
                sx={{ color: theme.brand2[500], textTransform: "none" }}
              >
                Cancel
              </Typography>
            </MuiButton>
            <Button loading={savingImage} onClick={handleSaveClick}>
              Save
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};
